import Api from '.';
import { endpoints } from './endpoints';

/**
 * Stage 9 — Nominees.
 *
 * In transition: legacy methods (getNomineeRelationshipDropdown, etc.) target
 * the on-prem backend. The new RESTful contract uses /nominees collection
 * verbs. Legacy methods are retired as Phase B migrates the callers.
 */
export default class NomineeApi extends Api {
    // --- Legacy on-prem contract -------------------------------------------
    getNomineeRelationshipDropdown(data) {
        return this.fetch(this.buildUrl(endpoints.nominee.getNomineeRelationshipDropdown), 'POST', JSON.stringify(data));
    }
    getNomineeDetailsById(data) {
        return this.fetch(this.buildUrl(endpoints.nominee.getNomineeDetailsById), 'POST', JSON.stringify(data));
    }
    bindState(data) {
        return this.fetch(this.buildUrl(endpoints.nominee.bindState), 'POST', JSON.stringify(data));
    }
    bindCity(data) {
        return this.fetch(this.buildUrl(endpoints.nominee.bindCity), 'POST', JSON.stringify(data));
    }
    updateNomineeInfo(data) {
        return this.fetch(this.buildUrl(endpoints.nominee.updateNomineeInfo), 'POST', JSON.stringify(data));
    }
    verifyNomineePanDetails(data) {
        return this.fetch(this.buildUrl(endpoints.nominee.verifyNomineePanDetails), 'POST', JSON.stringify(data));
    }

    // --- New eKYC 3.0 contract ---------------------------------------------

    // Stage 9 is post-auth: the nominee controller reads the numeric `userId`
    // (X-User-Id header on GET/DELETE/no-nominee, body `userId`/`leadId`-alias
    // on POST/PUT) — NOT the Stage 1-2 Guid leadId. Stamp `field: 'userId'`
    // so the transport sends the canonical identifier, mirroring personalApi.

    /** GET /nominees (X-User-Id header) */
    list() {
        return this.fetch(
            this.buildUrl(endpoints.stages.nominees.list),
            'GET', undefined,
            { lead: { mode: 'header', field: 'userId' } },
        );
    }

    /** POST /nominees */
    create(nominee) {
        return this.fetch(
            this.buildUrl(endpoints.stages.nominees.create),
            'POST', JSON.stringify(nominee),
            { lead: { mode: 'body', field: 'userId' } },
        );
    }

    /** PUT /nominees/{id} */
    update(id, nominee) {
        return this.fetch(
            this.buildUrl(endpoints.stages.nominees.update(id)),
            'PUT', JSON.stringify(nominee),
            { lead: { mode: 'body', field: 'userId' } },
        );
    }

    /** DELETE /nominees/{id} (X-User-Id header) */
    remove(id) {
        return this.fetch(
            this.buildUrl(endpoints.stages.nominees.remove(id)),
            'DELETE', undefined,
            { lead: { mode: 'header', field: 'userId' } },
        );
    }

    /** POST /nominees/verify-pan — BE command requires the field `pan` ([Required] string Pan). */
    verifyPan(panNumber) {
        return this.fetch(
            this.buildUrl(endpoints.stages.nominees.verifyPan),
            'POST', JSON.stringify({ pan: panNumber }),
            { lead: { mode: 'body' } },
        );
    }

    /**
     * GET /nominees/opt-out-declaration (X-User-Id header) — the Nominee Opt-out
     * Declaration popup copy and whether this lead already declared:
     * { title, text, consentLabel, version, declared, declaredAt }.
     */
    getOptOutDeclaration() {
        return this.fetch(
            this.buildUrl(endpoints.stages.nominees.optOutDeclaration),
            'GET', undefined,
            { lead: { mode: 'header', field: 'userId' } },
        );
    }

    /**
     * POST /nominees/no-nominee (X-User-Id header) — records the Nominee Opt-out
     * Declaration. Idempotent server-side; refused (400) while a nominee exists.
     */
    declareNone() {
        return this.fetch(
            this.buildUrl(endpoints.stages.nominees.none),
            'POST', JSON.stringify({}),
            { lead: { mode: 'header', field: 'userId' } },
        );
    }

    /**
     * GET /nominee/getStateCityFromPincode?pincode=XXXXXX
     *
     * Pincode → address lookup (legacy-parity, restored in eKYC 3.0 from
     * `ekyc.pincode_master`). Replaces the old on-prem POST call. Returns the
     * unwrapped envelope data: { pincode, city, district, state, stateCode }.
     * Throws ApiError on 400 (blank pincode) / 404 (unknown pincode).
     */
    getStateCityFromPincode(pincode) {
        const pin = encodeURIComponent(String(pincode == null ? '' : pincode).trim());
        return this.fetch(
            this.buildUrl(`${endpoints.stages.nominees.pincodeLookup}?pincode=${pin}`),
            'GET',
        );
    }
}
